// Operator tokens (Agent Platform 9E) — personal access tokens (kpo_…) for
// `kazi ops` and operator scripts. Each operator manages their OWN tokens;
// the secret is shown exactly once. Exchanging a PAT re-validates the
// operator every time, so revoking here (or deactivating the operator) kills
// access within minutes.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Form, Input, InputNumber, Modal, Popconfirm, Space, Table, Tag, Typography, message,
} from 'antd';
import { KeyOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);

interface OperatorToken {
  id: string;
  name: string;
  expiresAt: string;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
}

const OperatorTokensPage: React.FC = () => {
  const [rows, setRows] = useState<OperatorToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [minting, setMinting] = useState(false);
  const [minted, setMinted] = useState<{ name: string; token: string } | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_V1}/platform/operator-tokens`);
      setRows(data.tokens || []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load tokens.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const mint = async () => {
    const v = await form.validateFields();
    setMinting(true);
    try {
      const { data } = await axios.post(`${API_V1}/platform/operator-tokens`, {
        name: v.name.trim(),
        expiresInDays: v.expiresInDays,
      });
      setMinted({ name: data.name, token: data.token });
      setOpen(false);
      form.resetFields();
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not mint the token.');
    } finally {
      setMinting(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await axios.delete(`${API_V1}/platform/operator-tokens/${id}`);
      message.success('Token revoked.');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not revoke the token.');
    }
  };

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <KeyOutlined /> Operator tokens
        </Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Mint token
          </Button>
        </Space>
      </Space>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Your personal credentials for kazi ops and operator scripts."
        description={
          <>
            Store one with <Typography.Text code>kazi ops login --token kpo_…</Typography.Text> then run{' '}
            <Typography.Text code>kazi ops health</Typography.Text>,{' '}
            <Typography.Text code>kazi ops sandboxes create</Typography.Text>,{' '}
            <Typography.Text code>kazi ops certifications list</Typography.Text>. Tokens exchange for
            15-minute platform sessions on every use — revoking here cuts access immediately.
          </>
        }
      />
      {minted && (
        <Alert
          type="warning"
          closable
          onClose={() => setMinted(null)}
          style={{ marginBottom: 16 }}
          message={`Token "${minted.name}" — shown once, copy it now`}
          description={<Typography.Text code copyable>{minted.token}</Typography.Text>}
        />
      )}
      <Table<OperatorToken>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: 'Name', dataIndex: 'name' },
          {
            title: 'Status', width: 110,
            render: (_, r) => (r.revokedAt
              ? <Tag>revoked</Tag>
              : new Date(r.expiresAt) < new Date()
                ? <Tag color="orange">expired</Tag>
                : <Tag color="green">active</Tag>),
          },
          {
            title: 'Expires', dataIndex: 'expiresAt', width: 180,
            render: (v: string) => fmtDateTime(v),
          },
          {
            title: 'Last used', dataIndex: 'lastUsedAt', width: 180,
            render: (v?: string | null) => (v ? fmtDateTime(v) : 'never'),
          },
          {
            title: '', width: 100,
            render: (_, r) => (!r.revokedAt ? (
              <Popconfirm title="Revoke this token?" onConfirm={() => revoke(r.id)}>
                <Button size="small" danger>Revoke</Button>
              </Popconfirm>
            ) : null),
          },
        ]}
      />
      <Modal title="Mint an operator token" open={open} onCancel={() => setOpen(false)} onOk={mint} okText="Mint" confirmLoading={minting}>
        <Form form={form} layout="vertical" initialValues={{ expiresInDays: 30 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, min: 2 }]}
            extra="What this token is for, e.g. “laptop kazi ops”, “seed script”.">
            <Input placeholder="laptop kazi ops" />
          </Form.Item>
          <Form.Item name="expiresInDays" label="Expires in (days)" rules={[{ required: true }]}>
            <InputNumber min={1} max={90} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OperatorTokensPage;
export { OperatorTokensPage };
